import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import type { Chat as ChatType, Message, EnrichedMessage } from "@/frontend/lib/types";
import { transformMessages } from "@/frontend/lib/message-transformer";
import { ChatView, type ChatViewRef } from "./ChatView";

interface ChatProps {
  selectedChatId?: string;
  activeRightTab?: "incidents" | "askAi";
  onRightTabChange?: (tab: "incidents" | "askAi") => void;
}

export interface ChatRef {
  scrollToDate: (date: string) => void;
}

export const Chat = forwardRef<ChatRef, ChatProps>(({ selectedChatId, activeRightTab, onRightTabChange }, ref) => {
  const [chat, setChat] = useState<ChatType | null>(null);
  const [messages, setMessages] = useState<EnrichedMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatViewRef = useRef<ChatViewRef>(null);

  useEffect(() => {
    if (!selectedChatId) {
      setChat(null);
      setMessages([]);
      return;
    }

    const fetchChatData = async () => {
      setIsLoading(true);
      try {
        const [chatResponse, messagesResponse] = await Promise.all([
          fetch(`/api/chats/${selectedChatId}`),
          fetch(`/api/chats/${selectedChatId}/messages`),
        ]);

        if (chatResponse.ok && messagesResponse.ok) {
          const chatData = await chatResponse.json();
          const messagesData = await messagesResponse.json();

          setChat(chatData);
          setMessages(transformMessages(messagesData.messages));
        }
      } catch (error) {
        console.error("Error fetching chat data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatData();
  }, [selectedChatId]);

  useImperativeHandle(ref, () => ({
    scrollToDate: (date: string) => {
      chatViewRef.current?.scrollToDate(date);
    },
  }));

  if (!selectedChatId) {
    return (
      <div className="bg-card border rounded-lg p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">Select a chat to view messages</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-card border rounded-lg p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="bg-card border rounded-lg p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">Chat not found</p>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-lg h-full overflow-hidden">
      <ChatView
        ref={chatViewRef}
        chat={chat}
        messages={messages}
        activeRightTab={activeRightTab}
        onRightTabChange={onRightTabChange}
      />
    </div>
  );
});
